import React, { Fragment, useContext, useEffect, useState } from 'react';
import { useMatch } from 'react-router-dom';
import TextEditor from '../../../components/text-editor';
import { GlobalContext, handleError } from '../../../context/Provider';
import henceforthApi from '../../../utils/henceforthApi';
import Spinner from '../../../components/common/spinner';
import BreadCrumb from '../../../components/common/BreadCrumb';
import { editFaqType } from '../productInterface';
import commonArray from '../../../components/common/commonArray';
import { Button } from '../../../components/ui/button';
import { HelpCircle, Save, AlertCircle } from 'lucide-react';

const EditProductFaq: React.FC = () => {
  const match = useMatch('/product/:product_id/faq/edit/:faq_id');
  const { authState, loading, setLoading, authDispatch, language_data, toastMessage } =
    useContext(GlobalContext);

  const breadCrumbPath = [
    {
      name: `${language_data?.[authState.lang]?.common_home || 'Home'}`,
      url: `/`,
      active: '',
    },
    {
      name: `${language_data?.[authState.lang]?.common_product || 'Product'} ${language_data?.[authState.lang]?.common_list || 'List'
        }`,
      url: `/products/1`,
      active: '',
    },
    {
      name: `${language_data?.[authState.lang]?.common_product || 'Product'} ${language_data?.[authState.lang]?.common_deatil || 'Detail'
        }`,
      url: `/product/${match?.params.product_id}`,
      active: '',
    },
    {
      name: `${language_data?.[authState.lang]?.common_edit || 'Edit'} ${language_data?.[authState.lang]?.common_product || 'Product'
        } ${language_data?.[authState.lang]?.common_faq || 'FAQ'}`,
      url: ``,
      active: 'not-allowed',
    },
  ];

  const [getLoading, setGetLoading] = useState(false);
  const [answerErr, setAnswerErr] = useState<string>('');
  const [questionErr, setQuestionErr] = useState<string>('');
  const [editFaq, setEditFaq] = useState<editFaqType>({
    _id: match?.params.faq_id,
    product_id: match?.params.product_id,
    question: '',
    answer: '',
    language: 'ENGLISH',
  } as editFaqType);

  const handleChange = (e: any) => {
    const name = e?.target?.name ?? 'answer';
    const value = e?.target?.value ?? e;
    if (name === 'question') setQuestionErr('');
    if (name === 'answer') setAnswerErr('');
    setEditFaq((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editFaq.answer && !editFaq.question) {
      setQuestionErr(
        `${language_data?.[authState.lang]?.common_question || 'Question'} ${language_data?.[authState.lang]?.common_is || 'is'
        } ${language_data?.[authState.lang]?.common_required || 'required'}`
      );
      setAnswerErr(
        `${language_data?.[authState.lang]?.common_answer || 'Answer'} ${language_data?.[authState.lang]?.common_is || 'is'
        } ${language_data?.[authState.lang]?.common_required || 'required'}`
      );
      return;
    }
    if (editFaq.answer.trim() === '<p><br></p>' && !editFaq.question) {
      setQuestionErr(
        `${language_data?.[authState.lang]?.common_question || 'Question'} ${language_data?.[authState.lang]?.common_is || 'is'
        } ${language_data?.[authState.lang]?.common_required || 'required'}`
      );
      setAnswerErr(
        `${language_data?.[authState.lang]?.common_answer || 'Answer'} ${language_data?.[authState.lang]?.common_is || 'is'
        } ${language_data?.[authState.lang]?.common_required || 'required'}`
      );
      return;
    }
    if (!editFaq.question) {
      return setQuestionErr(
        `${language_data?.[authState.lang]?.common_question || 'Question'} ${language_data?.[authState.lang]?.common_is || 'is'
        } ${language_data?.[authState.lang]?.common_required || 'required'}`
      );
    }
    if (editFaq.answer.trim() === '<p><br></p>') {
      return setAnswerErr(
        `${language_data?.[authState.lang]?.common_answer || 'Answer'} ${language_data?.[authState.lang]?.common_is || 'is'
        } ${language_data?.[authState.lang]?.common_required || 'required'}`
      );
    }
    if (editFaq.answer.trim().length < 7 || editFaq.answer.trim() === '<p></p>') {
      return setAnswerErr(
        `${language_data?.[authState.lang]?.common_answer || 'Answer'} ${language_data?.[authState.lang]?.common_is || 'is'
        } ${language_data?.[authState.lang]?.common_required || 'required'}`
      );
    }
    if (!editFaq.question.trim()) {
      return setQuestionErr(
        `${language_data?.[authState.lang]?.common_please || 'Please'} ${language_data?.[authState.lang]?.common_write || 'write'
        } ${language_data?.[authState.lang]?.common_something || 'something'} ${language_data?.[authState.lang]?.common_in || 'in'
        } ${language_data?.[authState.lang]?.common_question || 'question'}`
      );
    }
    if (!editFaq.answer.trim()) {
      return setAnswerErr(
        `${language_data?.[authState.lang]?.common_please || 'Please'} ${language_data?.[authState.lang]?.common_write || 'write'
        } ${language_data?.[authState.lang]?.common_something || 'something'} ${language_data?.[authState.lang]?.common_in || 'in'
        } ${language_data?.[authState.lang]?.common_answer || 'answer'}`
      );
    }

    setLoading(true);
    try {
      const editFaqRes = await henceforthApi.Product.editProductDetailsDynamically(
        commonArray.productOtherDetails[3],
        editFaq
      );
      toastMessage(editFaqRes.message || 'FAQ updated successfully');
      window.history.back();
    } catch (err) {
      handleError(err, 'active', authDispatch);
    } finally {
      setLoading(false);
    }
  };

  const productFaq = async () => {
    setGetLoading(true);
    try {
      const faqRes = (
        await henceforthApi.Product.getProductDetailsAllDynamics(
          commonArray.productOtherDetails[3],
          match?.params.faq_id,
          ''
        )
      ).data.data[0];
      setEditFaq((prev) => ({
        ...prev,
        question: faqRes.question,
        answer: faqRes.answer,
      }));
    } catch (err: any) {
      console.log(err?.response?.body?.error_description);
      handleError(err, '', authDispatch);
    } finally {
      setGetLoading(false);
    }
  };

  useEffect(() => {
    productFaq();
  }, []);

  return (
    <Fragment>
      <BreadCrumb pathNameDeclare={breadCrumbPath} />

      <div className="w-full px-4 sm:px-6 lg:px-8 py-4 space-y-4">
        {getLoading ? (
          <div className="min-h-[300px] flex items-center justify-center">
            <Spinner color="text-[#1AA7DE]" />
          </div>
        ) : (
          <div className="bg-white rounded-2xl border border-slate-200/80 shadow-xs p-4 sm:p-4 space-y-4">
            {/* Header */}
            <div className="flex items-center gap-3.5 pb-4 border-b border-slate-100">
              <div className="w-10 h-10 rounded-xl bg-sky-50 text-[#1AA7DE] flex items-center justify-center shrink-0 shadow-2xs">
                <HelpCircle className="w-5 h-5" />
              </div>
              <div>
                <h2 className="text-base sm:text-lg font-bold text-slate-900 tracking-tight m-0">
                  {language_data?.[authState.lang]?.common_edit || 'Edit'}{' '}
                  {language_data?.[authState.lang]?.common_product || 'Product'}{' '}
                  {language_data?.[authState.lang]?.common_faq || 'FAQ'}
                </h2>
                <p className="text-xs text-slate-500 font-medium m-0 mt-0.5">
                  Update customer question and formatted answer
                </p>
              </div>
            </div>

            {/* Form */}
            <form onSubmit={handleSubmit} className="space-y-5">
              {/* Question */}
              <div className="space-y-1.5">
                <label className="block text-xs sm:text-sm font-bold text-slate-700 tracking-wide">
                  {language_data?.[authState.lang]?.common_question || 'Question'}{' '}
                  <span className="text-rose-500">*</span>
                </label>
                <input
                  type="text"
                  name="question"
                  value={editFaq.question}
                  onChange={handleChange}
                  placeholder={`${language_data?.[authState.lang]?.common_add || 'Add'} ${language_data?.[authState.lang]?.common_your || 'your'
                    } ${language_data?.[authState.lang]?.common_question || 'question'} ${language_data?.[authState.lang]?.common_here || 'here'
                    }`}
                  className={`w-full px-4 py-2.5 rounded-xl border text-xs sm:text-sm font-medium transition-all focus:outline-none focus:ring-2 ${questionErr
                      ? 'border-rose-300 bg-rose-50/30 text-rose-900 focus:ring-rose-200 focus:border-rose-500'
                      : 'border-slate-200 bg-slate-50/50 hover:bg-white focus:bg-white text-slate-800 focus:ring-[#1AA7DE]/20 focus:border-[#1AA7DE]'
                    }`}
                />
                {questionErr && (
                  <div className="flex items-center gap-1.5 text-xs font-semibold text-rose-600 pt-1">
                    <AlertCircle className="w-3.5 h-3.5 shrink-0" />
                    <span>{questionErr}</span>
                  </div>
                )}
              </div>

              {/* Answer */}
              <div className="space-y-1.5">
                <label className="block text-xs sm:text-sm font-bold text-slate-700 tracking-wide">
                  {language_data?.[authState.lang]?.common_answer || 'Answer'}{' '}
                  <span className="text-rose-500">*</span>
                </label>
                <div
                  className={`rounded-xl overflow-hidden border transition-all ${answerErr
                      ? 'border-rose-300 ring-2 ring-rose-200'
                      : 'border-slate-200 focus-within:border-[#1AA7DE] focus-within:ring-2 focus-within:ring-[#1AA7DE]/20'
                    }`}
                >
                  <TextEditor value={editFaq.answer} onChange={handleChange} />
                </div>
                {answerErr && (
                  <div className="flex items-center gap-1.5 text-xs font-semibold text-rose-600 pt-1">
                    <AlertCircle className="w-3.5 h-3.5 shrink-0" />
                    <span>{answerErr}</span>
                  </div>
                )}
              </div>

              {/* Action Buttons */}
              <div className="flex items-center justify-end gap-2.5 pt-3 border-t border-slate-100">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => window.history.back()}
                  disabled={loading}
                >
                  {language_data?.[authState.lang]?.common_cancel || 'Cancel'}
                </Button>
                <Button
                  type="submit"
                  variant="default"
                  size="sm"
                  disabled={loading}
                  className="gap-1.5"
                >
                  {loading ? (
                    <Spinner color="text-white" />
                  ) : (
                    <>
                      <Save className="w-4 h-4" />
                      <span>{language_data?.[authState.lang]?.common_save || 'Save FAQ'}</span>
                    </>
                  )}
                </Button>
              </div>
            </form>
          </div>
        )}
      </div>
    </Fragment>
  );
};

export default EditProductFaq;